/**
 * Compiled commit program — the pure, DOM-free half of the default-column live
 * commit: the `CommitBundle` that `DomReconciler` executes.
 *
 * `DefaultBinder.onBind` historically FUSED two things: it decided *what* the
 * per-column commit does (which cell to mount / recycle / remove, then rebind)
 * AND applied those decisions to the live DOM in one interleaved pass. This
 * module lifts the decision half out as a pure `CommitBundle`: the same
 * positional `Changeset` edit script `onBind` already computes is *compiled*
 * into an ordered structure program plus a per-target cell-directive list.
 * `DomReconciler` then executes the program against a decision-free host
 * (`CommitHost`), so no structural decision survives above that interface.
 *
 * The `Changeset` is DEMOTED to the compiler's input, NOT retired — the
 * SceneDiff-driven structure compiler (`compileSceneCommit`, the key-based
 * diff) is the other input. This positional compiler is a strict mirror of `assembleCommitOps` (commit-op-list.ts): same
 * edit-script replay, `replace`→`recycle`, `insert`→`mount`, `delete`→`remove`,
 * untouched→`keep`. The op-list stays the observe-only referee; this
 * bundle is the LIVE program.
 *
 * ## What the structure compilers emit — NAMED DEFERRALS
 * The `CellDirective` union carries `resize` / `span` / `skip` so the applier's
 * VM is total, but the structure compilers emit ONLY `rebind`. The reveal set,
 * the shimmer set, the geometry section and the pending/active double-buffer
 * are out of scope here; the other directive tags come from
 * `classifyCellDirectives` and are honored only behind the SDK 2.0 switch (the
 * render skip stays dark inside `applyPlanFullRebind`, untouched).
 */

import type { Option } from "@tento-lona/sheets";
import { invariant, nonnull } from "@tento-core/assert";
import { alignKeys, type Changeset, KeyAlignment } from "@tento-core/changeset";
import type { CellNode, SceneChange, SceneDiff } from "@tento-lona/sheets/platform";

/**
 * One structure op the commit performs against the cell pool. `slot` is the
 * DRAIN-TIME index (the `Changeset.index`) the host resolves the live cell at —
 * the bundle never holds a cell reference (learning 2026-06-18: bind the
 * drain-time occupant, never precompute).
 *
 * - `keep`    — a surviving target cell, untouched structurally (inert in the
 *   applier; present for classification parity with `assembleCommitOps`).
 * - `mount`   — a fresh cell spliced in at `slot` (`Changeset` insert).
 * - `recycle` — the cell at `slot` reused in place for a new row (`Changeset`
 *   replace); the host must fire `notifyCellRecycled`.
 * - `remove`  — the cell at `slot` removed (`Changeset` delete); `key` is the
 *   OLD row wire-string that occupied it (empty for a never-bound cell — the
 *   host resolves the cell by `slot`, so the key is classification-only).
 */
export type CommitStructureOp =
  | { readonly tag: "keep"; readonly key: string; readonly slot: number }
  | { readonly tag: "mount"; readonly key: string; readonly slot: number }
  | { readonly tag: "recycle"; readonly key: string; readonly slot: number }
  | { readonly tag: "remove"; readonly key: string; readonly slot: number };

/**
 * One per-cell directive the commit performs after structure settles. The
 * structure compilers emit only `rebind` (→ `applyPlanFullRebind`); the rest,
 * which only `classifyCellDirectives` produces, are NAMED DEFERRALS
 * (see the module header) kept in the union so the applier's switch is total.
 */
export type CellDirective =
  | { readonly tag: "rebind"; readonly key: string } // → applyPlanFullRebind
  | { readonly tag: "resize"; readonly key: string } // deferred: never emitted by a structure compiler
  | { readonly tag: "span"; readonly key: string } // deferred: never emitted by a structure compiler
  | { readonly tag: "skip"; readonly key: string }; // deferred: never emitted by a structure compiler

/**
 * The compiled commit program for one column's `onBind` pass. `structure` drives
 * the cell-pool mutations (mount / recycle / remove) in CHANGESET order followed
 * by the inert `keep` ops; `cells` carries exactly one directive per non-removed
 * target cell, in TARGET order (1:1 with the render plans / rows the applier
 * rebinds).
 */
export interface CommitBundle {
  readonly structure: readonly CommitStructureOp[];
  readonly cells: readonly CellDirective[];
}

/**
 * Compile a `CommitBundle` from the SAME inputs `onBind` already computes: the
 * positional `Changeset` edit script, the `rid` of each currently-rendered cell
 * (`oldRowIds`, in cell order, `null` for a never-bound cell), and the target
 * row wire-strings (in target order). Pure and serializable-friendly: no DOM, no
 * cell references — only slots and keys.
 *
 * Mirrors `assembleCommitOps`'s replay so the classification is identical:
 * `insert`→`mount`, `replace`→`recycle`, `delete`→`remove`, untouched→`keep`.
 * The mutating ops come out in CHANGESET order (each carrying its `Changeset`
 * index as `slot`), which is exactly the order the inline `onBind` structural
 * loop mutated `$cells`; the applier walking `structure` therefore reproduces
 * the same splice sequence byte-for-byte.
 */
export function compileCommitBundle(input: {
  readonly changes: readonly Changeset[];
  readonly oldRowIds: readonly Option<string>[];
  readonly targetKeys: readonly string[];
}): CommitBundle {
  const { changes, oldRowIds, targetKeys } = input;

  const inserted = new Set<string>();
  const replaced = new Set<string>();
  const structure: CommitStructureOp[] = [];

  // Replay the edit script against a mirror of the live row-id sequence so a
  // `delete` resolves the row-id occupying that slot at splice time — exact even
  // when earlier inserts/replaces shift indices ahead of it (mirror of
  // `assembleCommitOps`). Mutating ops are emitted in changeset order.
  const working: Option<string>[] = [...oldRowIds];
  for (const change of changes) {
    switch (change.type) {
      case "insert": {
        const key = nonnull(change.value, "commit-bundle: insert change without value");
        inserted.add(key);
        working.splice(change.index, 0, key);
        structure.push({ tag: "mount", key, slot: change.index });
        break;
      }
      case "replace": {
        const key = nonnull(change.value, "commit-bundle: replace change without value");
        replaced.add(key);
        working[change.index] = key;
        structure.push({ tag: "recycle", key, slot: change.index });
        break;
      }
      case "delete": {
        const gone = working[change.index] ?? null;
        working.splice(change.index, 1);
        // A never-bound cell (null rid) still diffs as a delete and still gets
        // removed from the DOM — the host resolves it by `slot`, so an empty key
        // here is inert (classification-only), matching how `assembleCommitOps`
        // omits it from the op-list's removes.
        structure.push({ tag: "remove", key: gone ?? "", slot: change.index });
        break;
      }
    }
  }

  // Untouched target cells → `keep`, in TARGET order, appended AFTER the
  // changeset-ordered mutations. Keeps are inert in the applier (no DOM), so
  // appending them cannot perturb the byte-identical mount/recycle/remove
  // ordering; they exist so the bundle classifies every target key (keep ↔
  // surviving `cell-update` in `assembleCommitOps`).
  for (const [i, key] of targetKeys.entries()) {
    if (!inserted.has(key) && !replaced.has(key)) {
      structure.push({ tag: "keep", key, slot: i });
    }
  }

  // Exactly one `rebind` per non-removed target cell, in target order (removes
  // are OLD cells, never targets, so every target cell rebinds). This compiler
  // emits only `rebind`; the deferred directive tags are never produced here.
  const cells: CellDirective[] = targetKeys.map((key) => ({ tag: "rebind", key }));

  return { structure, cells };
}

// ── SceneDiff-driven structure ─────────────────────────────────────

/**
 * Why compiling a scene-driven bundle can FAIL: the scene identity model keys
 * cells by row wire-string, so a duplicate key (two live cells claiming the
 * same row, or a target listing the same row twice) makes the anchor/membership
 * math undercount silently. Rather than emit a wrong bundle, `compileSceneCommit`
 * returns a discriminated failure so the caller falls back to the positional
 * bundle for that bind (+ a loud breadcrumb). No silent dedup.
 */
export type SceneCommitFailure =
  | "duplicate-target-keys"
  | "duplicate-pool-rids"
  | "scene-pool-desync";

/**
 * Result of `compileSceneCommit`. On success the compiled `bundle` is byte-for-
 * byte applicable by `DomReconciler` (same shape `compileCommitBundle` yields);
 * `bootstrap` records whether the compile fell back to pool-vs-target membership
 * (either `prevSceneKeys == null` or a precondition mismatch), which the caller
 * surfaces in its breadcrumb.
 */
export type SceneCommitResult =
  | { readonly ok: true; readonly bundle: CommitBundle; readonly bootstrap: boolean }
  | { readonly ok: false; readonly reason: SceneCommitFailure };

/**
 * Compile a `CommitBundle` from the column's `SceneDiff` (the scene's key-based
 * scene diff) + the live cell-pool arrangement — the scene-driven counterpart
 * to the positional `compileCommitBundle`. Where the positional compiler replays a
 * Levenshtein edit script, this one is scene-driven and SURVIVOR-PRESERVING:
 *
 *   - membership (which rows are structurally new / gone) comes from the
 *     `sceneDiff.cells` insert / remove changes (bootstrap: pool-vs-target),
 *   - arrangement (the slot program that realizes it) comes from an LCS-anchored
 *     gap alignment over `poolRids` / `targetKeys`.
 *
 * The behavioral difference from the positional path — the whole point of the
 * dark dual-run — is the SLIDE: where positional would recycle a survivor's cell
 * in place (destroying its bound content) to absorb a displacing insert, the
 * scene keeps that survivor's cell and expresses the change as mount + remove,
 * letting the survivor slide. In-place swaps (no survivor displaced) still
 * collapse to a recycle, so they stay byte-equal to positional (the referee
 * calls them "exact"); only displacing swaps diverge ("slide-classified").
 *
 * NOTE on the §2 brief prose: the intended verdicts (in-place swaps EXACT,
 * displacing swaps SLIDE) require the LCS-anchored gap alignment below, not the
 * literal "remove-all then mount-all then survivors" phase order — that order
 * would classify every swap as a slide, contradicting the exact battery. This
 * is the faithful realization of the brief's referee verdicts.
 */
export function compileSceneCommit(input: {
  readonly sceneDiff: SceneDiff;
  readonly targetKeys: readonly string[];
  readonly poolRids: readonly Option<string>[];
  readonly prevSceneKeys: Option<readonly string[]>;
}): SceneCommitResult {
  const { sceneDiff, targetKeys, poolRids, prevSceneKeys } = input;

  // Duplicate integrity — the scene Maps undercount on a repeated key, so a
  // wrong bundle would be worse than no bundle. Fail loud instead.
  if (hasDuplicate(targetKeys)) return { ok: false, reason: "duplicate-target-keys" };
  // Unchanged keys (the steady-state bind): the pool IS the target, so it has
  // no null and no duplicate either — one pass instead of three.
  const unchanged = KeyAlignment.same(poolRids, targetKeys);
  const poolKeys = unchanged ? targetKeys : poolRids.filter((k): k is string => k != null);
  if (!unchanged && hasDuplicate(poolKeys)) return { ok: false, reason: "duplicate-pool-rids" };

  // Precondition: the retained prev-scene cell keys must be the same multiset as
  // the live non-null pool rids. A mismatch means the retained scene drifted from
  // the pool (e.g. a bind threw before `commitScene`), so trust the pool: fall
  // back to bootstrap membership. `prevSceneKeys == null` is bootstrap by
  // construction (first bind / no retained scene).
  const bootstrap = prevSceneKeys == null ||
    !multisetEqual(prevSceneKeys, poolKeys);

  // Membership (which rows are structurally new / gone) is the SceneDiff's
  // decision — derived from `sceneDiff.cells` (non-bootstrap) or pool-vs-target
  // (bootstrap: `diffScene(null, scene)` reports every cell as an insert, which
  // would double-mount over a populated pool). The ARRANGEMENT below reads
  // pool/target order directly (the row-keyed diff maps do not carry pool order).
  const insertSet = deriveInsertSet(sceneDiff, poolKeys, targetKeys, bootstrap);

  const structure = compileSceneStructure(poolRids, targetKeys);

  // Coverage guard: every scene-inserted row MUST receive a cell (mount or an
  // in-place recycle). This always holds when the sceneDiff and pool agree; a
  // violation means the two desynced past what the precondition caught — fail
  // safe (caller falls back to the positional bundle) rather than drop a row.
  const provisioned = new Set<string>();
  for (const op of structure) {
    if (op.tag === "mount" || op.tag === "recycle") provisioned.add(op.key);
  }
  for (const key of insertSet) {
    if (!provisioned.has(key)) return { ok: false, reason: "scene-pool-desync" };
  }

  const cells: CellDirective[] = targetKeys.map((key) => ({ tag: "rebind", key }));
  return { ok: true, bundle: { structure, cells }, bootstrap };
}

/**
 * The rows the SceneDiff declares structurally NEW for this bind. Non-bootstrap:
 * the `insert` cell changes (keyed by row wire-string). Bootstrap: target keys
 * absent from the live pool (the `diffScene(null, …)` all-insert report is
 * discarded — it can't tell a real insert from a pool survivor).
 */
function deriveInsertSet(
  sceneDiff: SceneDiff,
  poolKeys: readonly string[],
  targetKeys: readonly string[],
  bootstrap: boolean,
): ReadonlySet<string> {
  const insertSet = new Set<string>();
  if (bootstrap) {
    const poolSet = new Set(poolKeys);
    for (const key of targetKeys) if (!poolSet.has(key)) insertSet.add(key);
    return insertSet;
  }
  for (const change of sceneDiff.cells) {
    if (change.key.kind !== "cell") continue;
    if (change.tag === "insert") insertSet.add(change.key.row.toString());
  }
  return insertSet;
}

/**
 * Build the ordered structure program that transforms the live pool arrangement
 * (`poolRids`) into `targetKeys` while preserving the cells of LCS-anchor
 * survivors. The emitted ops, replayed in order, reproduce `targetKeys`; a
 * displacing insert is expressed as mount + remove (survivor slides), while an
 * in-place swap collapses to a recycle.
 */
function compileSceneStructure(
  poolRids: readonly Option<string>[],
  targetKeys: readonly string[],
): CommitStructureOp[] {
  const n = poolRids.length;
  const m = targetKeys.length;

  // Unchanged keys: every cell is an anchor, so the program is all `keep`
  // (what the walk below yields for them, without the bookkeeping).
  if (KeyAlignment.same(poolRids, targetKeys)) {
    return targetKeys.map((key, slot): CommitStructureOp => ({ tag: "keep", key, slot }));
  }

  const { anchorPool, anchorTarget } = lcsAnchors(poolRids, targetKeys);

  // Pair dead pool slots with needed target positions gap-by-gap (between
  // consecutive anchors), aligned to the END of the gap: the last dead slot
  // recycles into the last needed position and so on backwards; whatever is
  // left over at the FRONT is removed (extra dead) or mounted (extra needed).
  // End-alignment is not a preference — it is what the positional
  // `Changeset.make` backtrack does: walking from the tail it prefers
  // `replace` at every step and only falls back to insert/delete once one side
  // is exhausted, so its recycles are tail-aligned and its extra mounts /
  // removes land at the head of the run. The two compilers therefore agree on
  // WHICH target keys get fresh cells, not just on the final arrangement. The
  // front-aligned pairing this replaces mounted the tail of an unequal gap
  // where positional mounted its head — the same final DOM, a different mount
  // set — which the referee (correctly) refused to classify as a slide: every
  // fixture→server sheet switch tripped the SCENE_COMMIT breaker on the
  // `mounts` axis. Equal-length gaps and one-sided gaps (pure insert, pure
  // delete, the displacing-swap slide) are unaffected.
  const recycledPool = new Array<boolean>(n).fill(false);
  const mountTarget = new Array<boolean>(m).fill(false);
  {
    let pi = 0;
    let tj = 0;
    while (pi < n || tj < m) {
      const dead: number[] = [];
      const need: number[] = [];
      while (pi < n && !anchorPool[pi]) { dead.push(pi); pi += 1; }
      while (tj < m && !anchorTarget[tj]) { need.push(tj); tj += 1; }
      const paired = Math.min(dead.length, need.length);
      // The last `paired` dead pool slots recycle; the first unpaired needed
      // target slots mount.
      for (const slot of dead.slice(dead.length - paired)) recycledPool[slot] = true;
      for (const slot of need.slice(0, need.length - paired)) mountTarget[slot] = true;
      if (pi < n && anchorPool[pi]) pi += 1;
      if (tj < m && anchorTarget[tj]) tj += 1;
    }
  }

  const structure: CommitStructureOp[] = [];
  const working: Option<string>[] = [...poolRids];

  // (1) removes — dead-and-not-recycled slots, descending (a lower remove can't
  // shift a higher pending slot). `key` is the drain-time occupant ("" for a
  // null-rid slot), matching the positional compiler's remove semantics.
  const removeSlots: number[] = [];
  for (let s = 0; s < n; s++) if (!anchorPool[s] && !recycledPool[s]) removeSlots.push(s);
  removeSlots.sort((a, b) => b - a);
  for (const slot of removeSlots) {
    structure.push({ tag: "remove", key: working[slot] ?? "", slot });
    working.splice(slot, 1);
  }

  // (2+3) walk target positions left→right; `slot == ti` since positions
  // 0..ti-1 are already finalized. A needed-and-unpaired position mounts a fresh
  // cell; an anchor keeps; a recycled dead cell (working[ti] is the old key) or a
  // reordered survivor recycles in place.
  for (const [ti, key] of targetKeys.entries()) {
    if (mountTarget[ti]) {
      working.splice(ti, 0, key);
      structure.push({ tag: "mount", key, slot: ti });
    } else if (working[ti] === key) {
      structure.push({ tag: "keep", key, slot: ti });
    } else {
      structure.push({ tag: "recycle", key, slot: ti });
      working[ti] = key;
    }
  }

  return structure;
}

/**
 * Longest-common-subsequence anchors of the pool (null never matches) and
 * target key sequences. The walk's tie-break prefers near-diagonal matches
 * (advance the lagging pointer on a tie) so the anchor set coincides with the
 * survivors the positional edit script keeps — a pure reshuffle therefore
 * recycles positionally (byte-equal to positional), while a displacing insert
 * that positional would recycle over a survivor leaves that survivor anchored
 * (the slide).
 *
 * The table is `alignKeys`'s (shared with `Changeset.make`): unchanged keys
 * anchor everything in O(n); otherwise the common prefix and suffix anchor
 * directly and only the changed middle gets a banded suffix-cost table. The
 * walk compares costs (`cost = remaining length − 2·LCS`), so "skipping the
 * pool key keeps the longer LCS" reads "skipping it costs less"; the anchors
 * are the ones the full LCS table gave.
 */
function lcsAnchors(
  poolRids: readonly Option<string>[],
  targetKeys: readonly string[],
): { anchorPool: boolean[]; anchorTarget: boolean[] } {
  const n = poolRids.length;
  const m = targetKeys.length;
  const aligned = alignKeys(poolRids, targetKeys, "indel", "from-start");
  if (aligned.kind === "equal") {
    return { anchorPool: new Array<boolean>(n).fill(true), anchorTarget: new Array<boolean>(m).fill(true) };
  }
  const { prefix, suffix, table } = aligned;
  const anchorPool = new Array<boolean>(n).fill(false);
  const anchorTarget = new Array<boolean>(m).fill(false);
  for (let k = 0; k < prefix; k++) {
    anchorPool[k] = true;
    anchorTarget[k] = true;
  }
  for (let k = 1; k <= suffix; k++) {
    anchorPool[n - k] = true;
    anchorTarget[m - k] = true;
  }
  let i = prefix;
  let j = prefix;
  while (i < n - suffix && j < m - suffix) {
    if (poolRids[i] != null && poolRids[i] === targetKeys[j]) {
      anchorPool[i] = true;
      anchorTarget[j] = true;
      i += 1;
      j += 1;
      continue;
    }
    const skipPool = table.at(i + 1 - prefix, j - prefix);
    const skipTarget = table.at(i - prefix, j + 1 - prefix);
    if (skipPool < skipTarget || (skipPool === skipTarget && i < j)) i += 1;
    else j += 1;
  }
  return { anchorPool, anchorTarget };
}

function hasDuplicate(keys: readonly string[]): boolean {
  return new Set(keys).size !== keys.length;
}

export function multisetEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  if (KeyAlignment.same(a, b)) return true;
  const counts = new Map<string, number>();
  for (const k of a) counts.set(k, (counts.get(k) ?? 0) + 1);
  for (const k of b) {
    const c = counts.get(k);
    if (c == null) return false;
    if (c === 1) counts.delete(k);
    else counts.set(k, c - 1);
  }
  return counts.size === 0;
}

// ── per-cell directive classification ──────────────────────────────

/**
 * Classify the per-cell directive for every target cell of a scene-driven commit
 * — the per-cell counterpart to the all-`rebind` `cells` the structure compilers
 * (`compileCommitBundle` / `compileSceneCommit`) still emit. Pure: reads the
 * SELECTED structure program + the column's `SceneDiff` and returns exactly one
 * directive per target key, in target order (1:1 with the cells the applier
 * walks).
 *
 * The load-bearing rule is that a freshly MOUNTED or RECYCLED cell (whose
 * selected-structure op is NOT `keep`) always `rebind`s — a recycled/mounted cell
 * carries no valid prior content, so it can never skip its first bind into that
 * slot. This kills the keyed-leak class and makes the early skip flip safe even
 * over POSITIONAL structure, where a displaced survivor's cell is recycled in
 * place and MUST rebind despite the scene diff calling the row unchanged. Only a
 * surviving `keep` consults the diff:
 *
 *   - `bootstrap`                        → rebind (no trustworthy prior scene)
 *   - op is not `keep` (mount / recycle) → rebind (fresh cell, first bind)
 *   - absent from the diff (unchanged)   → skip
 *   - `insert`                           → rebind (defensive; a keep shouldn't insert)
 *   - `update` content/binding changed   → rebind
 *   - `update` layout-only changed       → resize
 *   - `update` span-mode-only changed    → span
 *   - anything else                      → rebind (defensive)
 *
 * ## Attribute-only mutations
 * An attribute-only change (the row's `attr:` bag moved with no `data:` value
 * write) reaches here as a `keep` op with an `update` whose `contentChanged` is
 * true — `scene-diff` sets it from the stamp's new `attrVersion` facet (the same
 * `(values, attr)` pair LR6 keys its skip on), because an attribute change still
 * re-renders the cell. It therefore maps to `rebind` through the
 * content/binding arm above — never a wrongful `skip`. No dedicated arm is
 * needed: routing the attr verdict through the diff's `ChangeKind` keeps the
 * diff the single classifier the directives consume.
 *
 * `compileSceneCommit`'s `cells` output is UNCHANGED (still all-`rebind`); this is
 * a separate post-step so the two paths can be refereed against each other.
 */
export function classifyCellDirectives(input: {
  readonly structure: readonly CommitStructureOp[];
  readonly sceneDiff: SceneDiff;
  readonly targetKeys: readonly string[];
  readonly bootstrap: boolean;
}): readonly CellDirective[] {
  const { structure, sceneDiff, targetKeys, bootstrap } = input;

  // Which structure op lands each target key at its final slot. `remove` ops
  // carry an OLD occupant key (a vanished row), never a target key, so
  // mount / recycle / keep fully cover the target set.
  const tagByKey = new Map<string, CommitStructureOp["tag"]>();
  for (const op of structure) {
    if (op.tag === "remove") continue;
    tagByKey.set(op.key, op.tag);
  }

  // The scene-diff entry per row wire-string (cell changes only), keyed the same
  // way `deriveInsertSet` reads them (`change.key.row.toString()`).
  const changeByKey = new Map<string, SceneChange<CellNode>>();
  for (const change of sceneDiff.cells) {
    if (change.key.kind !== "cell") continue;
    changeByKey.set(change.key.row.toString(), change);
  }

  const cells: CellDirective[] = targetKeys.map((key): CellDirective => {
    if (bootstrap) return { tag: "rebind", key };
    // A freshly mounted / recycled cell has no valid prior content — always its
    // first bind, so never skip it. Only a surviving `keep` consults the diff.
    if (tagByKey.get(key) !== "keep") return { tag: "rebind", key };

    const change = changeByKey.get(key) ?? null;
    if (change == null) return { tag: "skip", key }; // unchanged survivor
    if (change.tag === "update") {
      const what = change.what;
      if (what.contentChanged || what.bindingChanged) return { tag: "rebind", key };
      if (what.layoutChanged) return { tag: "resize", key };
      if (what.spanModeChanged) return { tag: "span", key };
      return { tag: "rebind", key }; // defensive: an update carrying no flags
    }
    return { tag: "rebind", key }; // insert / remove / defensive
  });

  invariant(
    cells.length === targetKeys.length,
    "commit-bundle: classifyCellDirectives produced the wrong directive count",
  );
  return cells;
}
